import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useAppSelector } from "src/hooks/useStoreHooks";
import { ChatDTO, ChatMessageDTOExtended, messengerApi } from "src/store/messenger";
import { GroupedVirtuoso } from "react-virtuoso";
import { groupMessagesByDate } from "src/close-part/features/Messenger/lib";
import { MessageItem } from "../message-item";
import s from "./styles.module.scss";
import { DateSeparator } from "../date-separator";
import { VirtuosoCustomItem } from "./virtuoso-custom-item";

type Props = {
  chatInfo: ChatDTO;
  messageList: ChatMessageDTOExtended[];
  loadMore: VoidFunction;
};

const MessageListGroupedVirtuoso: FC<Props> = ({
  chatInfo,
  messageList,
  loadMore,
}) => {
  const virtuosoRef = useRef(null);
  const [sendLastReadMessageIdMutation] =
    messengerApi.useSendLastReadMessageMutation();

  const me = useAppSelector((state) => state.user.userInfo);

  const lastMessage = useMemo(() => {
    if (!messageList || !messageList.length) return null;

    const lastIndex = messageList.length - 1;

    return messageList[lastIndex];
  }, [messageList]);

  const amITheSenderOfLastMessage = lastMessage?.user?.id === me?.unique_id;

  const messagesByDate = useMemo(
    () => groupMessagesByDate(messageList || []),
    [messageList],
  );

  const groupCounts = useMemo(
    () => messagesByDate.map((group) => group.messageIds.length),
    [messagesByDate],
  );

  const readAllMessages = useCallback(() => {
    const areAllMessagesRead = !lastMessage || !lastMessage.isUnread;

    if (areAllMessagesRead) return;

    sendLastReadMessageIdMutation({
      messageId: lastMessage.id,
      chatId: lastMessage.chat_id,
    });
  }, [lastMessage, sendLastReadMessageIdMutation]);

  useEffect(() => {
    readAllMessages();
  }, [readAllMessages]);

  const onFollowOutputHandler = useCallback(
    (atBottom: boolean) => (atBottom || amITheSenderOfLastMessage ? "auto" : false),
    // NOTE: подписываемся на lastMessage если он меняется, виртуалайзер скроллит вниз
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastMessage],
  );

  const renderItemContent = useCallback(
    (messageIndex: number) => {
      const currentMessage = messageList[messageIndex];
      // [CHAT] TODO: подумать нужно ли обработать ошибку?
      if (!currentMessage) return;
      const nextMessage = messageList[messageIndex + 1];
      const isNextMessgeHaveSameSender =
        nextMessage && nextMessage.user?.id === currentMessage.user?.id;
      const isOutgoingMessage = currentMessage.user?.id === me?.unique_id;

      // NOTE: Если следующее сообщение - от другого пользователя, значит у текущего сообщения должен появиться уголок
      const shouldShowCorner = !isNextMessgeHaveSameSender;

      // NOTE: Если следующее сообщение - от другого пользователя и это не currentUser, значит у текущего сообщения
      // должен появиться аватар
      const shouldShowAvatar = !!(!isNextMessgeHaveSameSender && !isOutgoingMessage);

      return (
        <MessageItem
          message={currentMessage}
          chatVariant={chatInfo?.type}
          shouldShowAvatar={shouldShowAvatar}
          shouldShowCorner={shouldShowCorner}
          isOutgoingMessage={isOutgoingMessage}
          isLastItem={messageList.length - 1 === messageIndex}
        />
      );
    },
    [chatInfo?.type, me?.unique_id, messageList],
  );

  const renderSeparator = useCallback(
    (index: number) => {
      const node = messagesByDate[index];

      return <DateSeparator key={node.id} text={node.separatorText} />;
    },
    [messagesByDate],
  );

  // const calculateGroupsSoFar = useCallback((totalGroups, count) => {
  //   const groups = [];
  //   let i = 0;
  //   do {
  //     const group = totalGroups[i];
  //     groups.push(Math.min(group, count));
  //     count -= group;
  //     i++;
  //   } while (count > 0 && i <= totalGroups.length);

  //   return groups;
  // }, []);

  const onLoadMore = () => {
    loadMore();
  };

  // const loadMore = useCallback(() => {
  //   setLoading(true)
  //   return setTimeout(() => {
  //     loadedItems.current += 50
  //     setLoading(false)
  //     setCurrentGroupCounts(
  //       calculateGroupsSoFar(groupCounts, loadedItems.current)
  //     )
  //   }, 500)
  // }, [])

  return (
    <div className={s.root}>
      <GroupedVirtuoso
        ref={virtuosoRef}
        initialTopMostItemIndex={messageList.length}
        followOutput={onFollowOutputHandler}
        groupCounts={groupCounts}
        groupContent={renderSeparator}
        itemContent={renderItemContent}
        alignToBottom
        style={{
          overscrollBehavior: "contain",
        }}
        components={{ Item: VirtuosoCustomItem }}
        startReached={onLoadMore}
        // Тут можно сделать автопрочтение
        // endReached={}
        // NOTE:
        increaseViewportBy={500}
      />
    </div>
  );
};

export default MessageListGroupedVirtuoso;
